import apiClient, { extractData, handleApiError } from './api.js';

export class MenuService {
  /**
   * Fetch the primary menu for an outlet
   * GET /api/v1/menus/outlet/{outlet_id}
   * @param {string} outletId
   * @returns {Promise<Object>}
   */
  static async fetchPrimaryMenu(outletId) {
    try {
      const response = await apiClient.get(`/menus/outlet/${outletId}`);
      return extractData(response);
    } catch (error) {
      console.error('Error fetching primary menu:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch all menus for an outlet
   * GET /api/v1/menus/outlet/{outlet_id}/all
   * @param {string} outletId
   * @returns {Promise<Array>}
   */
  static async fetchMenus(outletId) {
    try {
      const response = await apiClient.get(`/menus/outlet/${outletId}/all`);
      return extractData(response) || [];
    } catch (error) {
      // 404 means no menus yet
      if (error.response?.status === 404) return [];
      console.error('Error fetching menus:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch menu items, optionally filtered by menu ID
   * GET /api/v1/menu-items?menu_id=...
   * @param {string} [menuId]
   * @returns {Promise<Array>} - items in the shape { itemID, Product_ID, outletId, menuId, name, price, image, category, category2 }
   */
  static async fetchMenuItems(menuId) {
    try {
      console.log(`[MenuService] Fetching items for menu: ${menuId}`);

      const params = {};
      if (menuId) params.menu_id = menuId;

      const response = await apiClient.get('/menu-items', { params });
      const rawItems = extractData(response) || [];

      console.log(`[MenuService] Received ${rawItems.length} items from API`);

      // The backend already transforms to frontend format (itemID, Product_ID, name, etc.)
      // per the API spec. Map with fallbacks for both formats.
      const items = rawItems.map((record) => ({
        itemID: record.itemID || record.item_id || '',
        Product_ID: record.Product_ID || record.item_id || '',
        outletId: record.outletId || record.outlet_id || '',
        menuId: record.menuId || record.menu_id || '',
        name: record.name || record.item_name || '',
        price: record.price || 0,
        image: record.image || record.image_link || '',
        category: record.category1 || record.category || [],
        category2: record.category2 || [],
      }));

      console.log(`[MenuService] Transformed ${items.length} items`);
      return items;
    } catch (error) {
      console.error('Error fetching menu items:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch a single menu item by its product/item ID
   * GET /api/v1/menu-items/{product_id}
   * @param {string} productId
   * @returns {Promise<Object>}
   */
  static async fetchMenuItem(productId) {
    try {
      const response = await apiClient.get(`/menu-items/${productId}`);
      const record = extractData(response);

      return {
        itemID: record.itemID || record.item_id || '',
        Product_ID: record.Product_ID || record.item_id || '',
        outletId: record.outletId || record.outlet_id || '',
        menuId: record.menuId || record.menu_id || '',
        name: record.name || record.item_name || '',
        price: record.price || 0,
        image: record.image || record.image_link || '',
        category: record.category1 || record.category || [],
        category2: record.category2 || [],
      };
    } catch (error) {
      console.error('Error fetching menu item:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create a scanned menu (from image URLs)
   * POST /api/v1/menus/scanned
   * @param {Object} menuData - { outlet_id, outlet_name, menu_name, menu_id, image_urls }
   * @returns {Promise<void>}
   */
  static async createScannedMenu(menuData) {
    try {
      const response = await apiClient.post('/menus/scanned', {
        outlet_id: menuData.outlet_id || menuData.outletId,
        outlet_name: menuData.outlet_name || menuData.outletName,
        menu_name: menuData.menu_name || menuData.menuName,
        menu_id: menuData.menu_id || menuData.menuId,
        image_urls: menuData.image_urls || menuData.imageUrls,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating scanned menu:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create a manual menu (no image scanning)
   * POST /api/v1/menus/manual
   * @param {Object} menuData - { outlet_id, outlet_name, menu_name, menu_id }
   * @returns {Promise<void>}
   */
  static async createMenu(menuData) {
    try {
      const response = await apiClient.post('/menus/manual', {
        outlet_id: menuData.outlet_id || menuData.outletId,
        outlet_name: menuData.outlet_name || menuData.outletName,
        menu_name: menuData.menu_name || menuData.menuName,
        menu_id: menuData.menu_id || menuData.menuId ||
          (menuData.menuName || menuData.menu_name || '').toLowerCase().replace(/\s+/g, '_') + '_' + Date.now(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating manual menu:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Check scan processing status
   * GET /api/v1/menus/{menu_id}/scan-status
   * @param {string} menuId
   * @returns {Promise<{scan_status: string}>}
   */
  static async getScanStatus(menuId) {
    try {
      const response = await apiClient.get(`/menus/${menuId}/scan-status`);
      return extractData(response);
    } catch (error) {
      console.error('Error getting scan status:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Set a menu as the primary menu for its outlet
   * PATCH /api/v1/menus/{menu_id}/set-primary
   * @param {string} menuId
   * @param {string} outletId
   * @returns {Promise<void>}
   */
  static async setPrimaryMenu(menuId, outletId) {
    try {
      await apiClient.patch(`/menus/${menuId}/set-primary`, { outlet_id: outletId });
    } catch (error) {
      console.error('Error setting primary menu:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Delete a menu
   * DELETE /api/v1/menus/{menu_id}
   * @param {string} menuId
   * @returns {Promise<boolean>}
   */
  static async deleteMenu(menuId) {
    try {
      await apiClient.delete(`/menus/${menuId}`);
      return true;
    } catch (error) {
      console.error('Error deleting menu:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Check menu processing status (for scanned menus)
   * GET /api/v1/menu-processing/status/{task_id}
   * @param {string} taskId - typically the menu_id
   * @returns {Promise<Object>}
   */
  static async getProcessingStatus(taskId) {
    try {
      const response = await apiClient.get(`/menu-processing/status/${taskId}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting processing status:', error);
      throw new Error(handleApiError(error));
    }
  }
}
